/* PatientBillingSection — patient-scoped invoice ledger.
 *
 * Issued from the doctor's clinic to the patient. Invoices may optionally
 * tag to one of the patient's cases (kit purchase, refinement, retainer)
 * but they're owned by the patient — multiple cases can roll up here.
 *
 * iter98.99af (2026-06) — Filter primitives aligned with the rest of the
 * doctor portal. The section now uses the shared `FilterSearchBar` +
 * `FilterChipRow` primitives from `@/components/doctor/FilterBar` so
 * every doctor-portal surface renders the same pill search, chip dot +
 * count pill, and DropdownMenu-based sort — no local re-invention.
 *
 * iter98.99ae — Ledger redesign per user brief:
 *   • Table header row above the list (Date · Invoice · Amount · Status
 *     · Actions).
 *   • Row simplified to 3 explicit actions: 1 primary CTA (Record
 *     payment), Edit icon, and a "More" dropdown menu that houses
 *     Print / WhatsApp / View details / Void.
 *   • Client-side pagination (8 per page) with a footer count.
 *
 * All actions remain strictly manual — nothing auto-sends. Draft state
 * is retired (see iter98.99ad); every invoice lands as "Unpaid".
 */
import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import {
    Receipt, Plus, CurrencyInr, Money, Printer, WhatsappLogo,
    Pencil, DotsThree, CaretLeft, CaretRight,
    Eye, ArrowsDownUp, X as XIcon,
} from "@phosphor-icons/react";
import InvoiceFormDialog from "@/components/case/InvoiceFormDialog";
import InvoiceDetailDialog from "@/components/case/InvoiceDetailDialog";
import RecordPaymentDialog from "@/components/case/RecordPaymentDialog";
import PickInvoiceForPaymentDialog from "@/components/case/PickInvoiceForPaymentDialog";
import InvoicePrintView from "@/components/case/InvoicePrintView";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";
import { confirmDialog } from "@/lib/confirm";

const STATUS_TONE = {
    /* iter98.99ad — Draft state retired; render both draft & sent as
     * "Unpaid" so the doctor sees a single active state. */
    draft:         { label: "Unpaid",        bg: "bg-primary/10 text-primary" },
    sent:          { label: "Unpaid",        bg: "bg-primary/10 text-primary" },
    viewed:        { label: "Viewed",        bg: "bg-sky-50 text-sky-700" },
    partial:       { label: "Partly paid",   bg: "bg-amber-50 text-amber-800" },
    paid:          { label: "Paid",          bg: "bg-emerald-50 text-emerald-800" },
    refunded:      { label: "Refunded",      bg: "bg-violet-50 text-violet-700" },
    uncollectible: { label: "Uncollectible", bg: "bg-slate-100 text-slate-700" },
    void:          { label: "Void",          bg: "bg-rose-50 text-rose-700 line-through" },
};

/* Client-side page size for the ledger. Chosen so the section stays
 * compact — most patients have <8 invoices anyway. */
const PAGE_SIZE = 8;

/* Filter chips — matches the doctor-portal chip grammar (dot + label +
 * count pill, per-chip tone). "Overdue" is a derived state
 * (balance>0 && due_at<today) rather than a persisted status. */
const CHIP_DEFS = [
    { id: "all",     label: "All",     tone: "primary" },
    { id: "unpaid",  label: "Unpaid",  tone: "primary" },
    { id: "paid",    label: "Paid",    tone: "emerald" },
    { id: "overdue", label: "Overdue", tone: "rose"    },
];

const SORT_MODES = [
    { id: "newest",    label: "Newest first" },
    { id: "oldest",    label: "Oldest first" },
    { id: "amount_hi", label: "Amount: high to low" },
    { id: "amount_lo", label: "Amount: low to high" },
];

function formatINR(amount) {
    if (amount === null || amount === undefined) return "—";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(amount);
}

function shortDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    } catch {
        return "—";
    }
}

function purposeSummary(items) {
    const list = items || [];
    if (list.length === 0) return "No line items";
    const first = list.slice(0, 2).map((li) => li.description).filter(Boolean).join(" · ");
    if (list.length <= 2) return first || "—";
    return `${first} +${list.length - 2}`;
}

function isOverdue(inv) {
    if (!inv.due_at) return false;
    if ((inv.balance || 0) <= 0) return false;
    if (["paid", "void", "refunded", "uncollectible"].includes(inv.status)) return false;
    try {
        return new Date(inv.due_at).getTime() < Date.now();
    } catch { return false; }
}

/* Shared WhatsApp message builder — pre-fills wa.me so the doctor's
 * own WhatsApp sends the message; no server-side send. */
function buildWhatsAppUrl({ digits, invoice, clinic }) {
    const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
    const lines = [];
    lines.push(`Hello ${invoice.patient_name || ""},`);
    lines.push("");
    lines.push(`This is regarding your invoice from ${clinic}.`);
    lines.push("");
    lines.push(`*Invoice:* ${invoice.code}`);
    lines.push(`*Total:* ${fmt(invoice.total)}`);
    if (invoice.amount_paid > 0 && invoice.balance > 0) {
        lines.push(`*Paid so far:* ${fmt(invoice.amount_paid)}`);
        lines.push(`*Balance due:* ${fmt(invoice.balance)}`);
    } else if (invoice.balance > 0) {
        lines.push(`*Balance due:* ${fmt(invoice.balance)}`);
    } else if (invoice.status === "paid") {
        lines.push("Payment received — *invoice fully settled.* Thank you!");
    }
    if (invoice.due_at && invoice.balance > 0) {
        lines.push(`*Due by:* ${new Date(invoice.due_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}`);
    }
    lines.push("");
    if (invoice.balance > 0) {
        lines.push("Please reach out if you have any questions about the bill or would like to arrange payment.");
    }
    lines.push("");
    lines.push("Thank you,");
    lines.push(clinic);
    return `https://wa.me/${digits}?text=${encodeURIComponent(lines.join("\n"))}`;
}


export default function PatientBillingSection({ patientId, patientName, cases = [] }) {
    const { user } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openCreate, setOpenCreate] = useState(false);
    const [openInvoiceId, setOpenInvoiceId] = useState(null);
    const [editInvoice, setEditInvoice] = useState(null);
    const [quickPayInvoice, setQuickPayInvoice] = useState(null);
    const [printInvoice, setPrintInvoice] = useState(null);
    const [showInvoicePicker, setShowInvoicePicker] = useState(false);
    /* Row-level per-action working ids so the More menu items can
     * disable themselves while a network call is in flight. */
    const [waSendingId, setWaSendingId] = useState(null);
    const [voidingId, setVoidingId] = useState(null);

    /* Toolbar state (iter98.99ae). */
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [sortMode, setSortMode] = useState("newest");
    const [page, setPage] = useState(1);

    const load = async () => {
        if (!patientId) return;
        setLoading(true);
        try {
            const r = await api.get("/doctor/invoices", { params: { patient_id: patientId } });
            setInvoices(r.data || []);
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [patientId]);

    /* Reset paginator whenever a filter narrows the result set. */
    useEffect(() => { setPage(1); }, [search, statusFilter, sortMode]);

    const summary = useMemo(() => {
        const live = invoices.filter((i) => i.status !== "void");
        return {
            count: invoices.length,
            outstanding: live.reduce((sum, i) => sum + (i.balance || 0), 0),
            billed: live.reduce((sum, i) => sum + (i.total || 0), 0),
            paid: live.reduce((sum, i) => sum + (i.amount_paid || 0), 0),
        };
    }, [invoices]);

    const caseCodeById = useMemo(() => {
        const m = {};
        for (const c of cases) m[c.id] = c.case_code || c.code || c.id.slice(0, 6);
        return m;
    }, [cases]);

    /* Chip counts — mirrors /doctor/billing grammar: dot + label + count
     * pill on every chip. Counts are pre-filter (no search) so they
     * remain stable while typing. */
    const chipCounts = useMemo(() => ({
        all:     invoices.length,
        unpaid:  invoices.filter((i) => ["draft", "sent", "viewed", "partial"].includes(i.status) && (i.balance || 0) > 0).length,
        paid:    invoices.filter((i) => i.status === "paid").length,
        overdue: invoices.filter(isOverdue).length,
    }), [invoices]);

    /* Filter, then sort, then paginate. Client-side because the
     * ledger is typically small (<20 rows). */
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return invoices.filter((inv) => {
            // Search: code + purpose + notes
            if (q) {
                const hay = [
                    inv.code || "",
                    purposeSummary(inv.line_items),
                    inv.notes || "",
                ].join(" ").toLowerCase();
                if (!hay.includes(q)) return false;
            }
            // Status filter
            if (statusFilter === "unpaid") {
                if (!["draft", "sent", "viewed", "partial"].includes(inv.status)) return false;
                if ((inv.balance || 0) <= 0) return false;
            } else if (statusFilter === "paid") {
                if (inv.status !== "paid") return false;
            } else if (statusFilter === "overdue") {
                if (!isOverdue(inv)) return false;
            }
            return true;
        });
    }, [invoices, search, statusFilter]);

    const sorted = useMemo(() => {
        const arr = [...filtered];
        arr.sort((a, b) => {
            if (sortMode === "newest") return (b.created_at || "").localeCompare(a.created_at || "");
            if (sortMode === "oldest") return (a.created_at || "").localeCompare(b.created_at || "");
            if (sortMode === "amount_hi") return (b.total || 0) - (a.total || 0);
            if (sortMode === "amount_lo") return (a.total || 0) - (b.total || 0);
            return 0;
        });
        return arr;
    }, [filtered, sortMode]);

    const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const pageStart = (page - 1) * PAGE_SIZE;
    const paged = sorted.slice(pageStart, pageStart + PAGE_SIZE);

    const openInvoice = invoices.find((i) => i.id === openInvoiceId);

    /* ── Row-level action handlers ── */
    const sendWhatsApp = async (inv) => {
        setWaSendingId(inv.id);
        try {
            const { data: patient } = await api.get(`/doctor/patients/${inv.patient_id}`);
            const digits = (patient?.phone || "").replace(/\D/g, "");
            if (!digits) {
                toast.error("This patient has no phone number saved. Add one to their profile first.");
                return;
            }
            const clinic = user?.doctor_clinic_name || user?.clinic_name || "your clinic";
            window.open(buildWhatsAppUrl({ digits, invoice: inv, clinic }), "_blank", "noopener,noreferrer");
            toast.success("Opening WhatsApp…");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not open WhatsApp");
        } finally {
            setWaSendingId(null);
        }
    };

    const voidInvoice = async (inv) => {
        const ok = await confirmDialog({
            title: "Void this invoice?",
            message: "This cannot be undone.",
            confirmText: "Void invoice",
            tone: "danger",
        });
        if (!ok) return;
        setVoidingId(inv.id);
        try {
            await api.post(`/doctor/invoices/${inv.id}/void`);
            toast.success(`Invoice ${inv.code} voided`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not void invoice");
        } finally {
            setVoidingId(null);
        }
    };

    const sortLabel = SORT_MODES.find((m) => m.id === sortMode)?.label || "Sort";

    return (
        <div data-testid="patient-billing-section" className="space-y-4">

            {/* ─── HEADER CARD: title, CTAs, stats ─── */}
            <div className="bg-card border border-border rounded-2xl bento-shadow overflow-hidden">
                <div className="px-6 pt-5 pb-4 flex items-start justify-between gap-3 border-b border-border/60">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <Receipt size={14} weight="duotone" className="text-primary" />
                            <h3 className="font-display text-lg font-semibold tracking-tight">Billing</h3>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Invoices from your clinic to {patientName || "this patient"} · <strong>{summary.count}</strong> on record
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {invoices.some((i) => ["sent", "viewed", "partial"].includes(i.status) && (i.balance || 0) > 0) && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setShowInvoicePicker(true)}
                                className="rounded-full text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800"
                                data-testid="patient-billing-record-payment"
                            >
                                <Money size={13} weight="duotone" className="mr-1" /> Record payment
                            </Button>
                        )}
                        <Button
                            size="sm"
                            onClick={() => setOpenCreate(true)}
                            className="rounded-full"
                            data-testid="patient-billing-new-invoice"
                        >
                            <Plus size={13} weight="bold" className="mr-1" /> New invoice
                        </Button>
                    </div>
                </div>

                {summary.count > 0 && (
                    <div className="grid grid-cols-3">
                        <StatBox label="Outstanding" value={formatINR(summary.outstanding)} sub="Pending collection" tone={summary.outstanding > 0 ? "rose" : "neutral"} />
                        <StatBox label="Paid" value={formatINR(summary.paid)} sub="Received to date" tone="emerald" divider />
                        <StatBox label="Billed (lifetime)" value={formatINR(summary.billed)} sub="Total invoiced" tone="primary" divider />
                    </div>
                )}
            </div>

            {/* ─── SHARED FILTER PRIMITIVES (iter98.99af) ─── matches the
                pill search + chip dot + count pill + DropdownMenu sort
                grammar used on /doctor/patients, /doctor/cases,
                /doctor/appointments, and /doctor/billing. */}
            {summary.count > 0 && (
                <>
                    <FilterSearchBar
                        query={search}
                        setQuery={setSearch}
                        placeholder="Search invoice #, item, notes…"
                        searchTestid="patient-billing-search"
                        testid="patient-billing-filter-bar"
                        rightSlot={
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        type="button"
                                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors"
                                        title="Sort invoices"
                                        data-testid="patient-billing-sort"
                                    >
                                        <ArrowsDownUp size={12} /> {sortLabel}
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="min-w-[190px]">
                                    <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Sort by</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuRadioGroup value={sortMode} onValueChange={setSortMode}>
                                        {SORT_MODES.map((m) => (
                                            <DropdownMenuRadioItem
                                                key={m.id}
                                                value={m.id}
                                                className="text-xs"
                                                data-testid={`patient-billing-sort-${m.id}`}
                                            >
                                                {m.label}
                                            </DropdownMenuRadioItem>
                                        ))}
                                    </DropdownMenuRadioGroup>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        }
                    />
                    <FilterChipRow
                        value={statusFilter}
                        onChange={setStatusFilter}
                        testid="patient-billing-filter-chips"
                        chipTestidPrefix="patient-billing-filter"
                        chips={CHIP_DEFS.map((c) => ({
                            id: c.id,
                            label: c.label,
                            tone: c.tone,
                            count: chipCounts[c.id] ?? 0,
                        }))}
                    />
                </>
            )}

            {/* ─── LIST CARD ─── */}
            <div className="bg-card border border-border rounded-2xl bento-shadow overflow-hidden">
            {loading ? (
                <div className="p-8 text-center text-xs text-muted-foreground">Loading…</div>
            ) : invoices.length === 0 ? (
                <div className="p-10 text-center" data-testid="patient-billing-empty">
                    <div className="w-12 h-12 rounded-full bg-secondary mx-auto mb-3 flex items-center justify-center">
                        <CurrencyInr size={20} weight="duotone" className="text-muted-foreground" />
                    </div>
                    <p className="text-sm text-foreground font-medium">No invoices for this patient yet</p>
                    <p className="text-xs text-muted-foreground mt-1 mb-4">Bill for the aligner kit, consultation, scan, or any add-on.</p>
                    <Button size="sm" variant="outline" onClick={() => setOpenCreate(true)} className="rounded-full" data-testid="patient-billing-create-first">
                        Create first invoice
                    </Button>
                </div>
            ) : sorted.length === 0 ? (
                <div className="p-10 text-center" data-testid="patient-billing-no-match">
                    <p className="text-sm text-foreground font-medium">No invoices match those filters</p>
                    <p className="text-xs text-muted-foreground mt-1">Try clearing search or picking a different chip.</p>
                </div>
            ) : (
                <>
                    {/* Table header — grid columns match every row below */}
                    <div className="grid grid-cols-[80px_1fr_170px_120px_190px] gap-3 px-6 py-2.5 bg-secondary/30 border-b border-border/40">
                        <ColHead>Date</ColHead>
                        <ColHead>Invoice</ColHead>
                        <ColHead className="text-right">Amount</ColHead>
                        <ColHead>Status</ColHead>
                        <ColHead className="text-right">Actions</ColHead>
                    </div>

                    <div className="divide-y divide-border/40">
                        {paged.map((inv) => (
                            <InvoiceRow
                                key={inv.id}
                                invoice={inv}
                                caseCode={inv.case_id ? caseCodeById[inv.case_id] : null}
                                overdue={isOverdue(inv)}
                                waSending={waSendingId === inv.id}
                                voiding={voidingId === inv.id}
                                onOpen={() => setOpenInvoiceId(inv.id)}
                                onEdit={() => setEditInvoice(inv)}
                                onRecordPayment={() => setQuickPayInvoice({ ...inv, patient_name: patientName })}
                                onPrint={() => setPrintInvoice(inv)}
                                onWhatsApp={() => sendWhatsApp(inv)}
                                onVoid={() => voidInvoice(inv)}
                            />
                        ))}
                    </div>

                    {/* Footer: count + pagination when needed */}
                    <div className="px-6 py-3 border-t border-border/40 bg-secondary/10 flex items-center justify-between">
                        <span className="text-[11px] text-muted-foreground" data-testid="patient-billing-count">
                            Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, sorted.length)} of {sorted.length}
                            {sorted.length !== invoices.length && ` (filtered from ${invoices.length})`}
                        </span>
                        {pageCount > 1 && (
                            <div className="flex items-center gap-1.5" data-testid="patient-billing-pagination">
                                <PaginationBtn
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    testId="patient-billing-page-prev"
                                >
                                    <CaretLeft size={11} weight="bold" />
                                </PaginationBtn>
                                {Array.from({ length: pageCount }).map((_, i) => (
                                    <PaginationBtn
                                        key={i}
                                        active={i + 1 === page}
                                        onClick={() => setPage(i + 1)}
                                        testId={`patient-billing-page-${i + 1}`}
                                    >
                                        {i + 1}
                                    </PaginationBtn>
                                ))}
                                <PaginationBtn
                                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                                    disabled={page === pageCount}
                                    testId="patient-billing-page-next"
                                >
                                    <CaretRight size={11} weight="bold" />
                                </PaginationBtn>
                            </div>
                        )}
                    </div>
                </>
            )}
            </div>

            {/* ─── Dialogs ─── */}
            <InvoiceFormDialog
                open={openCreate}
                onClose={() => setOpenCreate(false)}
                onSaved={() => { setOpenCreate(false); load(); }}
                patientId={patientId}
                patientName={patientName}
                cases={cases}
            />
            <InvoiceFormDialog
                open={!!editInvoice}
                onClose={() => setEditInvoice(null)}
                onSaved={() => { setEditInvoice(null); load(); }}
                patientId={patientId}
                patientName={patientName}
                cases={cases}
                defaultCaseId={editInvoice?.case_id || null}
                existingInvoice={editInvoice}
            />
            <InvoiceDetailDialog
                open={!!openInvoiceId}
                invoice={openInvoice}
                patientCases={cases}
                onClose={() => setOpenInvoiceId(null)}
                onChanged={load}
            />
            <RecordPaymentDialog
                open={!!quickPayInvoice}
                invoice={quickPayInvoice}
                onClose={() => setQuickPayInvoice(null)}
                onSaved={() => { setQuickPayInvoice(null); load(); }}
            />
            <InvoicePrintView
                open={!!printInvoice}
                invoice={printInvoice}
                onClose={() => setPrintInvoice(null)}
            />
            <PickInvoiceForPaymentDialog
                open={showInvoicePicker}
                invoices={invoices}
                patientName={patientName}
                onClose={() => setShowInvoicePicker(false)}
                onPick={(inv) => {
                    setShowInvoicePicker(false);
                    setQuickPayInvoice({ ...inv, patient_name: patientName });
                }}
            />
        </div>
    );
}

/* ─────────── StatBox — single summary tile ─────────── */
function StatBox({ label, value, sub, tone, divider }) {
    const toneClass = {
        rose:    "text-rose-700",
        emerald: "text-emerald-700",
        primary: "text-primary",
        neutral: "text-foreground",
    }[tone] || "text-foreground";
    return (
        <div className={`px-6 py-5 ${divider ? "border-l border-border/40" : ""}`}>
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">{label}</div>
            <div className={`font-display text-2xl font-extrabold tabular-nums tracking-tight ${toneClass}`}>{value}</div>
            {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
        </div>
    );
}


function ColHead({ children, className = "" }) {
    return (
        <div className={`text-[10px] font-bold uppercase tracking-widest text-muted-foreground ${className}`}>
            {children}
        </div>
    );
}


/* ─────────── InvoiceRow — clean 5-column grid ───────────
 *
 *   [ Date ] [ Invoice (code · purpose · due) ] [ Amount ] [ Status ] [ Actions ]
 *
 * Actions column houses at most 3 elements:
 *   • Primary CTA (Record payment) — visible only when balance > 0.
 *   • Edit icon — visible on mutable statuses.
 *   • More dropdown — Print / WhatsApp / View details / Void.
 */
function InvoiceRow({
    invoice, caseCode, overdue, waSending, voiding,
    onOpen, onEdit, onRecordPayment, onPrint, onWhatsApp, onVoid,
}) {
    const tone = overdue
        ? { label: "Overdue", bg: "bg-rose-50 text-rose-700" }
        : (STATUS_TONE[invoice.status] || STATUS_TONE.sent);
    const status = invoice.status;
    const canEdit = ["draft", "sent", "viewed", "partial"].includes(status);
    const canPay = ["draft", "sent", "viewed", "partial"].includes(status) && (invoice.balance || 0) > 0;
    const canWhatsApp = status !== "void";
    const canVoid = ["draft", "sent", "viewed", "partial"].includes(status);
    const dateStamp = shortDate(invoice.created_at || invoice.issued_at);

    return (
        <div
            className="grid grid-cols-[80px_1fr_170px_120px_190px] gap-3 items-center px-6 py-3.5 hover:bg-secondary/25 transition-colors cursor-pointer"
            onClick={onOpen}
            data-testid={`patient-invoice-row-${invoice.id}`}
        >
            {/* Date */}
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground tabular-nums">
                {dateStamp}
            </div>

            {/* Invoice details */}
            <div className="min-w-0">
                <div className="font-mono text-[11px] font-semibold text-primary/90 truncate">
                    {invoice.code}
                </div>
                <div className="text-sm font-semibold text-foreground truncate mt-0.5">
                    {purposeSummary(invoice.line_items)}
                </div>
                <div className="text-[10.5px] text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                    {caseCode && (
                        <span className="font-mono">{caseCode}</span>
                    )}
                    {caseCode && invoice.due_at && <span className="opacity-40">·</span>}
                    {invoice.due_at ? (
                        <span className={overdue ? "text-rose-700 font-medium" : ""}>
                            {overdue ? "Overdue since " : "Due "}{shortDate(invoice.due_at)}
                        </span>
                    ) : (!caseCode && <span className="italic">No due date</span>)}
                </div>
            </div>

            {/* Amount */}
            <div className="text-right">
                <div className="font-display text-base font-bold tabular-nums text-foreground">
                    {formatINR(invoice.total)}
                </div>
                {invoice.balance > 0 && !["void", "uncollectible", "refunded"].includes(status) && (
                    <div className="text-[11px] text-rose-700 font-semibold mt-0.5 tabular-nums">
                        {formatINR(invoice.balance)} due
                    </div>
                )}
                {invoice.balance === 0 && status === "paid" && (
                    <div className="text-[11px] text-emerald-700 font-semibold mt-0.5">Settled</div>
                )}
                {status === "refunded" && (
                    <div className="text-[11px] text-violet-700 font-semibold mt-0.5 tabular-nums">
                        {formatINR(invoice.amount_refunded || invoice.total)} refunded
                    </div>
                )}
            </div>

            {/* Status pill */}
            <div>
                <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${tone.bg}`}
                    data-testid={`patient-invoice-status-${invoice.id}`}
                >
                    <span className="w-1.5 h-1.5 rounded-full bg-current" />
                    {tone.label}
                </span>
            </div>

            {/* Actions — stop propagation so row-click doesn't swallow */}
            <div
                className="flex items-center justify-end gap-1.5"
                onClick={(e) => e.stopPropagation()}
                data-testid={`patient-invoice-row-actions-${invoice.id}`}
            >
                {canPay && (
                    <Button
                        size="sm"
                        onClick={onRecordPayment}
                        className="h-8 px-3 rounded-lg text-[11.5px] bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                        data-testid={`patient-invoice-record-payment-${invoice.id}`}
                    >
                        <Money size={11} weight="duotone" className="mr-1" /> Record payment
                    </Button>
                )}
                {canEdit && (
                    <button
                        type="button"
                        onClick={onEdit}
                        title="Edit line items, tax, notes"
                        data-testid={`patient-invoice-edit-${invoice.id}`}
                        className="h-8 w-8 rounded-lg border border-border bg-background flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
                    >
                        <Pencil size={12} weight="duotone" />
                    </button>
                )}

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            title="More actions"
                            data-testid={`patient-invoice-more-${invoice.id}`}
                            className="h-8 w-8 rounded-lg border border-border bg-background flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
                        >
                            <DotsThree size={16} weight="bold" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        className="w-52"
                        data-testid={`patient-invoice-more-menu-${invoice.id}`}
                    >
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">
                            Invoice actions
                        </DropdownMenuLabel>
                        <DropdownMenuItem
                            onClick={onOpen}
                            data-testid={`patient-invoice-menu-open-${invoice.id}`}
                        >
                            <Eye size={13} weight="duotone" className="mr-2" /> View details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={onPrint}
                            data-testid={`patient-invoice-menu-print-${invoice.id}`}
                        >
                            <Printer size={13} weight="duotone" className="mr-2" /> Print / PDF
                        </DropdownMenuItem>
                        {canWhatsApp && (
                            <DropdownMenuItem
                                onClick={onWhatsApp}
                                disabled={waSending}
                                data-testid={`patient-invoice-menu-whatsapp-${invoice.id}`}
                                className="text-emerald-700 focus:text-emerald-800 focus:bg-emerald-50"
                            >
                                <WhatsappLogo size={13} weight="duotone" className="mr-2" />
                                {waSending ? "Opening…" : "Send on WhatsApp"}
                            </DropdownMenuItem>
                        )}
                        {canVoid && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={onVoid}
                                    disabled={voiding}
                                    data-testid={`patient-invoice-menu-void-${invoice.id}`}
                                    className="text-rose-700 focus:text-rose-800 focus:bg-rose-50"
                                >
                                    {voiding ? (
                                        <>
                                            <XIcon size={12} weight="bold" className="mr-2 animate-pulse" />
                                            Voiding…
                                        </>
                                    ) : (
                                        <>
                                            <XIcon size={12} weight="bold" className="mr-2" />
                                            Void invoice
                                        </>
                                    )}
                                </DropdownMenuItem>
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}


function PaginationBtn({ onClick, disabled, active, testId, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            data-testid={testId}
            className={`h-7 min-w-[28px] px-2 rounded-md text-[11.5px] font-semibold border transition-colors ${
                active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background border-border text-foreground hover:border-foreground/20 disabled:opacity-40 disabled:cursor-not-allowed"
            }`}
        >
            {children}
        </button>
    );
}
